import type { FC } from 'react';
import React, { useState, useEffect, useMemo } from 'react';
import InputMask from 'react-input-mask';
import type { TrackingProp } from 'react-tracking';
import BEMHelper from '@ynap/bem-helper';
import { ValidatedField } from '@ynap/field';
import type { FieldRenderProps } from '@ynap/form';
import { Field as FormField } from '@ynap/form';
import { Tooltip } from '@ynap/notification';
import { CombinedSelect } from '@ynap/select';
import AddressFinder from './AddressFinder';
import { snakeToCamel, getMessageByKey, validate } from './utils';
import type { Messages, Field, AddressFinderParams, CheckoutAnalytics } from './@types';

type FieldFormat = {
    mask: string;
    placeholder: string;
};

export function getMaskFromField(field: Field, country: string): FieldFormat | null {
    if (country === 'JP' && field.format?.length === 1) {
        const format = field.format[0];
        return {
            mask: format.replace(/N/g, '9').replace(/A/g, 'a'),
            placeholder: format.replace(/N/g, '0').replace(/A/g, 'a'),
        };
    }

    return null;
}

export interface AddressFieldProps {
    innerKey: string;
    className: string;
    country: string;
    field: Field;
    initialValue?: string;
    messages?: Messages;
    errorStyle?: string;
    tracking?: TrackingProp;
    shouldValidateNonLatin: boolean;
    addressFinderParams: AddressFinderParams | null;
    foundValue?: string;
    isCheckoutLayout?: boolean;
    primaryCategory?: string;
    checkoutAnalytics?: CheckoutAnalytics;
    reduxForm: any;
    invalidFields?: string[];
}

type DropdownTouchedState = boolean | undefined;

export const AddressField: FC<AddressFieldProps> = ({
    addressFinderParams,
    checkoutAnalytics,
    className,
    country,
    errorStyle = 'error',
    field,
    foundValue,
    initialValue,
    innerKey,
    invalidFields,
    isCheckoutLayout,
    messages,
    primaryCategory,
    reduxForm = {},
    shouldValidateNonLatin,
    tracking,
}) => {
    const { displayType, type, values, wcsType } = field;
    const { syncErrors = {} } = reduxForm;
    const name = wcsType?.name || '';

    let displayName = snakeToCamel(displayType || type);
    displayName = getMessageByKey(messages?.addressForm?.display, displayName) || displayName;
    const selectLabel = (name !== 'personTitle' && messages?.addressForm?.select?.({ select: displayName })) || ' ';
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [dropdownTouched, setDropdownTouched] = useState<DropdownTouchedState>();
    const [externalErrors, setExternalErrors] = useState<string[]>([]);
    const helpMessage = getMessageByKey(messages?.addressForm?.help, displayName);
    const fieldFormat = useMemo(() => getMaskFromField(field, country), [field, country]);

    useEffect(() => {
        if (invalidFields) {
            setExternalErrors(invalidFields);
        }
    }, [invalidFields]);

    const options = Array.isArray(values)
        ? values.map((option) => {
              if (typeof option == 'string') {
                  return { key: option, value: option, label: option };
              }

              const { display, value } = option;
              return { key: value, value, label: display };
          })
        : [];

    const tooltipMessage = getMessageByKey(messages?.addressForm?.tooltips, name?.toLowerCase());

    const showTooltip = () => {
        setTooltipOpen(true);

        if (String(name).toLowerCase() === 'pccc' && tracking) {
            tracking.trackEvent({
                event: 'view tooltip - PCCC',
                effect: 'view more information',
                category: {
                    primaryCategory: 'checkout',
                    subCategory: 'tooltip information',
                },
            });
        }
    };

    const handleClickOutTooltip = () => {
        setTooltipOpen(false);
    };

    useEffect(() => {
        // add when mounted
        document.addEventListener('mousedown', handleClickOutTooltip);
        // return function to be called when unmounted
        return () => {
            document.removeEventListener('mousedown', handleClickOutTooltip);
        };
    }, []);

    const getDefaultLabel = () => {
        if (!field.mandatory && (!['personTitle', 'addressFinder'].includes(name) || !isCheckoutLayout)) {
            const optionalMessage = messages?.addressForm?.optional?.() || '';
            return `${displayName} ${optionalMessage}`;
        }

        return displayName;
    };

    const getCustomLabel = () => {
        const tooltipBEM = new BEMHelper('AddressFieldTooltip');

        return {
            LabelWrap: Tooltip,
            labelWrapProps: {
                type: 'info',
                message: tooltipMessage,
                defaultOpen: false,
                open: tooltipOpen,
                getArrowDimensions: () => ({ left: 0, width: 'auto' }),
            },
            labelName: displayName,
            extraElements: <span className={tooltipBEM('info-icon')} onClick={showTooltip}></span>,
        };
    };

    // To stop error being triggered by Klarna we force the + sign in the phone number. This can be adjusted by the user if incorrect
    const addPrefixToPhoneNumber = (value: string, name: string): string => {
        const canAddPrefixToNumber = syncErrors?.billingAddress?.isKlarna && name === 'phone1' && value.length > 2 && value.split('')[0] !== '+';

        return canAddPrefixToNumber ? `+${value}` : value;
    };

    const removeUsedExternalError = (field: any) => {
        setExternalErrors((prev) => prev.filter((error) => error !== field));
    };

    return (
        <FormField
            key={innerKey}
            removable
            foundValue={foundValue}
            name={name}
            readOnlyValue={field.value}
            validate={validate(messages, field, displayName, country, shouldValidateNonLatin, syncErrors?.billingAddress?.isKlarna, externalErrors)}
            value={initialValue || ''}
        >
            {({ errors, hint, onBlur, onChange, showErrorNotification, touched, value, ...rest }: FieldRenderProps) => {
                const showError = isCheckoutLayout && values ? dropdownTouched !== false : true;
                const error = touched && showError && errors?.[0] ? errors[0] : undefined;
                const enableSearch = name === 'state' || name === 'city';
                const labelData = { ...(tooltipMessage ? { customLabel: getCustomLabel() } : { label: getDefaultLabel() }) };
                const validatedFieldProps = {
                    ...labelData,
                    error,
                    errorStyle: values ? 'highlight' : errorStyle,
                    help: hint || helpMessage,
                    htmlFor: name,
                    className,
                    showErrorNotification,
                };

                const renderInput = () => {
                    if (values) {
                        return (
                            <CombinedSelect
                                key={name}
                                ariaLabel={'label' in labelData ? labelData.label : labelData.customLabel?.labelName}
                                emptyLabel={selectLabel}
                                enableSearch={enableSearch}
                                hasError={!!error}
                                id={name}
                                onClose={() => isCheckoutLayout && setDropdownTouched(true)}
                                options={options}
                                searchPlaceholder={messages?.addressForm?.search?.()}
                                value={value}
                                onChange={(value: string) => {
                                    onChange(value);
                                    onBlur(value);
                                    removeUsedExternalError(name);
                                }}
                                onDropdown={() => {
                                    onBlur(value);
                                    setDropdownTouched((status) => (status === undefined ? false : status));
                                }}
                            />
                        );
                    }

                    if (fieldFormat) {
                        return (
                            <InputMask
                                id={name}
                                mask={fieldFormat.mask}
                                maskChar={null}
                                onBlur={(ev: React.FocusEvent<HTMLInputElement>) => onBlur(ev.target.value)}
                                placeholder={fieldFormat.placeholder}
                                type="text"
                                value={value}
                                onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                                    onChange(ev.target.value);
                                    removeUsedExternalError(name);
                                }}
                                {...rest}
                            />
                        );
                    }

                    if (displayType === 'ADDRESS_FINDER' && addressFinderParams) {
                        return (
                            <AddressFinder
                                addressFinderParams={addressFinderParams}
                                checkoutAnalytics={checkoutAnalytics}
                                country={country}
                                primaryCategory={primaryCategory}
                                tracking={tracking}
                            />
                        );
                    }

                    return (
                        <input
                            id={name}
                            onBlur={(ev: React.FocusEvent<HTMLInputElement>) => onBlur(ev.target.value)}
                            type="text"
                            value={value}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                                onChange(addPrefixToPhoneNumber(ev.target.value, name));
                                removeUsedExternalError(name);
                            }}
                            {...rest}
                        />
                    );
                };

                return (
                    <ValidatedField {...validatedFieldProps}>
                        <div>{renderInput()}</div>
                    </ValidatedField>
                );
            }}
        </FormField>
    );
};
